import {DeleteItemCommand, DynamoDBClient, GetItemCommand, PutItemCommand, ScanCommand} from "@aws-sdk/client-dynamodb";
import {fromCognitoIdentityPool} from "@aws-sdk/credential-provider-cognito-identity";
import {IDENTITY_POOL_ID, REGION, TABLE_NAME, USER_POOL_ID} from "./config";
import {showErrorNotification, showSuccessNotification} from "../components/Elements/NotificationUtils";
import {deleteImageFromS3} from "./S3";
import { sizes } from "../assets/constant";



export const getAWSCredentials = (idToken) => {
    return fromCognitoIdentityPool({
        clientConfig: { region: REGION },
        identityPoolId: IDENTITY_POOL_ID,
        logins: {
            [`cognito-idp.${REGION}.amazonaws.com/${USER_POOL_ID}`]: idToken,
        },
    });
};

export const createDynamoDBClient = (idToken) => {
    return new DynamoDBClient({
        region: REGION,
        credentials: getAWSCredentials(idToken),
    });
};

// Fetch the latest highest ID from DynamoDB
const getLatestCarpetId = async (dynamoDBClient) => {
    const params = {
        TableName: TABLE_NAME,
        ProjectionExpression: "id",
    };

    try {
        const data = await dynamoDBClient.send(new ScanCommand(params));
        const ids = data.Items.map(item => parseInt(item.id.S)).filter(id => !isNaN(id));
        const maxId = ids.length > 0 ? Math.max(...ids) : 0;
        return (maxId + 1).toString();
    } catch (error) {
        console.error("Error fetching latest ID:", error);
        return "1"; // Default to '1' if no items are found
    }
};

const parseSize = (sizeStr) => {
    const [w, h] = sizeStr.split("x").map(Number);
    return { width: w, height: h };
};

const findMatchingStandardSizes = (actualSize, tolerance = 15) => {
    const { width, height } = parseSize(actualSize);

    return sizes.filter((std) => {
        const stdSize = parseSize(std);
        return (
            Math.abs(stdSize.width - width) <= tolerance ||
            Math.abs(stdSize.height - height) <= tolerance
        );
    });
};
export const saveCarpetData = async (data, idToken) => {
    const dynamoDBClient = createDynamoDBClient(idToken);

    // Generate new ID if it's null
    const carpetId = data.id ? data.id : await getLatestCarpetId(dynamoDBClient);
    const matchingStandardSizes = findMatchingStandardSizes(data.size);

    // Auto-generate tags and filter out empty/null ones
    const tags = [
        data.size,
        data.model,
        ...matchingStandardSizes,
    ].filter(tag => tag && tag.trim() !== "");

    const params = {
        TableName: TABLE_NAME,
        Item: {
            id: { S: carpetId },
            name: { S: data.name || "" },
            nameLower: { S: (data.name || "").toLowerCase() },
            model: { S: data.model || "" },
            size: { S: data.size || ""  },
            // massen: { S: data.massen },
            // color: { S: data.color || "" },
            // material: { S: data.material || "" },
            // manufacturing: { S: data.manufacturing || "" },
            // design: { S: data.design || "" },
            // usage: { S: data.usage || "" },
            height: { S: data.height || "" },
            width: { S: data.width || "" },
            orgPrice: { S: data.orgPrice || "" },
            acctPrice: { S: data.acctPrice || "" },
            priceOnRequest: { BOOL: data.priceOnRequest || false },
            shortDescription: { S: data.shortDescription || "" },
            shortDescriptionLower: { S: (data.shortDescription || "").toLowerCase() },
            detailedDescription: { S: data.detailedDescription || "" },
            images: { L: data.images.map((url) => ({ S: url })) },
            created_time: { S: data.created_time || new Date().toISOString() },
            updated_time: { S: new Date().toISOString() },
            updated_by: { S: data.updated_by || "Admin" },
            tags: { L: tags.map((tag) => ({ S: tag })) },
        },
    };

    try {
        const command = new PutItemCommand(params);
        await dynamoDBClient.send(command);
        console.log("Carpet data saved to DynamoDB.");
        showSuccessNotification("Teppich wurde gespeichert");
    } catch (error) {
        console.error("Error saving data to DynamoDB:", error);
        showErrorNotification("Teppich wurde nicht gespeichert")
    }
};


// Delete Carpet and its Images from DynamoDB and S3
export const deleteCarpetData = async (carpet) => {
    const idToken = localStorage.getItem("idToken"); // Ensure it's saved after user login

    const dynamoDBClient = createDynamoDBClient(idToken);

    // Delete images from S3
    for (const imageUrl of carpet.images) {
        await deleteImageFromS3(imageUrl, idToken);
    }

    // Delete item from DynamoDB
    const params = {
        TableName: TABLE_NAME,
        Key: {
            id: { S: carpet.id },
        },
    };

    await dynamoDBClient.send(new DeleteItemCommand(params));
    showSuccessNotification("Teppich wurde gelöscht");

};


const dynamoDBClientFetch = new DynamoDBClient({
    region: REGION,
    credentials: fromCognitoIdentityPool({
        clientConfig: {region: REGION},
        identityPoolId: IDENTITY_POOL_ID,
    }),
});
export const fetchCarpetsFromDynamoDB = async (page = 1, searchTerm = "", filtersQuery = "", sortBy = "date-newest", limit = 12) => {
    let params = {
        TableName: TABLE_NAME,
        //Limit: limit,
        FilterExpression: "#type <> :bannerType",
        ExpressionAttributeNames: {
            "#type": "type",
        },
        ExpressionAttributeValues: {
            ":bannerType": { S: "banner" },
        },
    };
    // if (filtersQuery.includes("size=")) {
    //     params.ExpressionAttributeNames["#tags"] = "tags";
    // }



// Parse filters from query string
    const filterConditions = [];
    const attributeValues = {};

    if (filtersQuery) {
        const filterGroups = filtersQuery.split("&"); // Separate categories (AND logic)

        filterGroups.forEach((group, groupIndex) => {
            const [category, values] = group.split("=");
            const valuesArray = values.split(","); // OR logic within a category

            // Construct OR condition for this category
            const orConditions = valuesArray.map((value, valueIndex) => {
                const placeholder = `:val${groupIndex}_${valueIndex}`;
                attributeValues[placeholder] = { S: value.trim() };
                if (category === "size") {
                    return `contains(tags, ${placeholder})`; // ✅ match on tags instead
                } else {
                    return `#${category} = ${placeholder}`;
                }
            }).join(" OR ");

            // Add the category to ExpressionAttributeNames
            if (category !== "size") {
                params.ExpressionAttributeNames[`#${category}`] = category;
            }


            // Wrap OR conditions in parentheses for proper AND logic
            filterConditions.push(`(${orConditions})`);
        });
    }

    // If any filters exist, append them to FilterExpression
    if (filterConditions.length > 0) {
        params.FilterExpression += ` AND ${filterConditions.join(" AND ")}`;
        params.ExpressionAttributeValues = { ...params.ExpressionAttributeValues, ...attributeValues };
    }

    if (searchTerm && searchTerm.trim() !== "") {
        const normalizedSearch = searchTerm.toLowerCase();
        params.FilterExpression += ` AND (contains(#nameLower, :searchTerm) OR contains(#shortDescriptionLower, :searchTerm) OR contains(id, :searchTerm))`;
        params.ExpressionAttributeValues[":searchTerm"] = { S: normalizedSearch };
        params.ExpressionAttributeNames["#nameLower"] = "nameLower";
        params.ExpressionAttributeNames["#shortDescriptionLower"] = "shortDescriptionLower";
    }

    try {

        const command = new ScanCommand(params);
        const { Items = [] } = await dynamoDBClientFetch.send(command);

        let carpets = Items.map((item) => ({
            id: item.id?.S || "",
            name: item.name?.S || "",
            model: item.model?.S || "",
            orgPrice: item.orgPrice?.S || "",
            acctPrice: item.acctPrice?.S || "",
            priceOnRequest: item.priceOnRequest.BOOL || false ,
            height: item.height?.S || "0",
            width: item.width?.S || "0",
            size: item.size?.S || "",
            // massen: item.massen?.S || "",
            // color: item.color?.S || "",
            // material: item.material?.S || "",
            // manufacturing: item.manufacturing?.S || "",
            // design: item.design?.S || "",
            // usage: item.usage?.S || "",
            images: item.images?.L?.map((img) => img.S) || [],
            shortDescription: item.shortDescription?.S || "",
            detailedDescription: item.detailedDescription?.S || "",
            created_time: item.created_time?.S || "",
            updated_time: item.updated_time?.S || "",
            updated_by: item.updated_by?.S || "",
            tags: item.tags?.L?.map(tag => tag.S) || [],
        }));
        carpets.sort((a, b) => {
            switch (sortBy) {
                case "date-oldest":
                    // oldest first
                    return new Date(a.created_time) - new Date(b.created_time);

                case "price-asc": {
                    // OnRequest carpets go to the end
                    if (a.priceOnRequest) return 1;
                    if (b.priceOnRequest) return -1;
                    // parse numbers
                    const pa = parseFloat(a.acctPrice || a.orgPrice || 0);
                    const pb = parseFloat(b.acctPrice || b.orgPrice || 0);
                    return pa - pb;
                }

                case "price-desc": {
                    if (a.priceOnRequest) return 1;
                    if (b.priceOnRequest) return -1;
                    const pa = parseFloat(a.acctPrice || a.orgPrice || 0);
                    const pb = parseFloat(b.acctPrice || b.orgPrice || 0);
                    return pb - pa;
                }

                case "date-newest":
                default:
                    // newest first
                    return new Date(b.created_time) - new Date(a.created_time);
            }
        });

        // ── 5) Slice out just this page ───────────────────────────────────────────────
        const total = carpets.length;
        const start = (page - 1) * limit;
        const paged = carpets.slice(start, start + limit);
        // ────────────────────────────────────────────────────────────────────────────────

        return {
            items: paged,
            total,                      // Gesamtzahl für Dein UI
            page,
            pageCount: Math.ceil(total / limit),
        };

       // {
       //     items: Carpet[],   // nur die aktuellen 12 (oder wieviel Du limitiert hast)
       //         total: number,     // Gesamtzahl aller gefilterten Teppiche
       //     page: number,      // Deine angefragte Seite
       //     pageCount: number, // Wie viele Gesamtseiten es gibt
       // }
    } catch (error) {
        console.error("Failed to fetch carpets from DynamoDB:", error);
        return [];
    }
};

export const fetchCarpetById = async (id) => {
    const params = {
        TableName: TABLE_NAME,
        Key: {
            id: {S: id},
        },
    };

    try {
        const command = new GetItemCommand(params);
        const {Item} = await dynamoDBClientFetch.send(command);

        if (!Item) {
            console.error("Carpet not found with ID:", id);
            return null;
        }
        // Map DynamoDB data to a clean object
        return {
            id: Item.id?.S || "",
            name: Item.name?.S || "",
            model: Item.model?.S || "",
            orgPrice: Item.orgPrice?.S || "0",
            acctPrice: Item.acctPrice?.S || "0",
            priceOnRequest: Item.priceOnRequest.BOOL || false ,
            height: Item.height?.S || "0",
            width: Item.width?.S || "0",
            size: Item.size?.S || "",
            // massen: Item.massen?.S || "",
            // color: Item.color?.S || "",
            // material: Item.material?.S || "",
            // manufacturing: Item.manufacturing?.S || "",
            // design: Item.design?.S || "",
            // usage: Item.usage?.S || "",
            images: Item.images?.L?.map((img) => img.S) || [],
            shortDescription: Item.shortDescription?.S || "",
            detailedDescription: Item.detailedDescription?.S || "",
            created_time: Item.created_time?.S || "",
            updated_time: Item.updated_time?.S || "",
            updated_by: Item.updated_by?.S || "",
            tags: Item.tags?.L?.map(tag => tag.S) || [],
        };

    } catch (error) {
        console.error("Failed to fetch carpet by ID:", error);
        return null;
    }
};


// Function to save the banner text in DynamoDB
export const saveBannerText = async (bannerText) => {
    const idToken = localStorage.getItem("idToken"); // Ensure it's saved after user login

    const dynamoDBClient = createDynamoDBClient(idToken);
    const params = {
        TableName: TABLE_NAME,
        Item: {
            id: { S: "banner" }, // Using a fixed ID for the banner
            type: { S: "banner" },
            text: { S: bannerText },
            updated_time: { S: new Date().toISOString() },
        },
    };

    try {
        const command = new PutItemCommand(params);
        await dynamoDBClient.send(command);
        showSuccessNotification("Nachricht wurde gespeichert");
    } catch (error) {
        console.error("Error saving banner text to DynamoDB:", error);
        showErrorNotification("Failed to save banner text");
    }
};

// Function to fetch the banner text from DynamoDB
export const fetchBannerText = async () => {
    const params = {
        TableName: TABLE_NAME,
        Key: {
            id: { S: "banner" },
        },
    };

    try {
        const command = new GetItemCommand(params);
        const { Item } = await dynamoDBClientFetch.send(command);

        return Item ? Item.text.S : "";
    } catch (error) {
        console.error("Failed to fetch banner text from DynamoDB:", error);
        return "";
    }
};